import React, { useState, useMemo, useEffect } from 'react';
import { Card, Form, Modal, Popover } from 'antd';
import { useQuery } from 'react-query';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DF from 'DisplayFramework';
import { isEqual } from 'lodash';
import {
  FinModification as FinModificationInterface,
  findFinModificationByAnyRefOrStatus,
  FindFinModificationByAnyRefOrStatusParams,
} from 'services/fincontract';
import ParListSelect from 'components/ParListSelect';
// import RestoreValidateButtons from './RestoreValidateButtons';
import moment from 'moment';
import ModalControllerNV from 'components/ModalControllerNV';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ContractDetailModal from 'pages/Accounting/Components/ContractDetailModal';
import TabFinModificationReview from 'pages/workflow/tabFinModificationReview';
import TabFinModificationRestoreModal from './FinModificationRestoreModal';

type Values = {
  finmodifications: FinModificationInterface[];
};

export type FinModificationProps = {
  isTab?: any;
  refetchProp?: any;
  isActive?: boolean;
};

const FinModification = ({
  ctrid,
  hideExtraAction,
  hideFilters,
  hideCtreferenceFilters,
  isTab,
  refetchProp,
  isActive
}: any) => {
  const [form] = Form.useForm();
  const [searchs, setsearchs] = useState(false);

  const initialSearchValues = {
    ctreference: '',
    ctrid: ctrid ?? 0,
    modevent: '',
    modstatus: '',
    modtype: '',
  };

  const [searchParams, setSearchParams] =
    useState<FindFinModificationByAnyRefOrStatusParams>(initialSearchValues);

  const fieldName = 'finmodifications';

  const { data, refetch, isFetching } = useQuery(
    ['findFinModificationByAnyRefOrStatus', searchParams, refetchProp],
    () =>
      findFinModificationByAnyRefOrStatus(searchParams)
        .then((res) => res.list || [])
        .catch((err) => []),
    { enabled: searchs || ctrid !== null && isActive === true }
  );

  const [selectedCtrid, setSelectedCtrid] = useState(null);

  const [selectedModevent, setSelectedModevent] = useState('');

  const handleCtridSelect = (ct: any) => {
    setSelectedCtrid(ct.ctrid);
    setSelectedModevent(ct.modevent);
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [fieldName]: data });
    }
  }, [form, data]);

  const columns = useMemo<DataGridColDef<FinModificationInterface>[]>(
    () => [
      { dataIndex: 'modid', filtrable: false, fixed: 'left', width: 80 },
      {
        dataIndex: 'modtypelabel',
        filtrable: 'modtype',
        fixed: 'left',
        width: 100,
        filterComponent: (
          <ParListSelect parcode="MODTYPE" style={{ width: '100%' }} />
        ),
        render: (value, record) =>
          record.modtype === 'SIMUL' ? (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <Modal
                  open={visible}
                  onCancel={() => {
                    setVisible(false);
                  }}
                  width={1200}
                  destroyOnClose
                  title={
                    <GetLabel
                      label={`TabFinModificationRestore ${record.ctreference}`}
                    />
                  }
                  footer={null}
                >
                  <Card>
                    <TabFinModificationReview
                      objectid={0}
                      subentityid={record.modid}
                      isFetching={isFetching}
                      hidefields
                      isActive={visible}
                    />
                  </Card>
                </Modal>
              )}
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{value}</a>
              )}
            />
          ) : (
            record.modtypelabel
          ),
      },
      {
        dataIndex: 'ctreference',
        filtrable: true && !hideCtreferenceFilters,
        fixed: 'left',
        render: (value, record) => (
          <ContractDetailModal
            ctreference={record.ctreference}
            ctrid={record.ctrid}
          />
        ),
        width: 150,
      },
      {
        dataIndex: 'eventlabel',
        filtrable: 'modevent',
        filterComponent: (
          <ParListSelect parcode="MODEVENT" style={{ width: '100%' }} />
        ),
        width: 150,
      },

      {
        dataIndex: 'modstatuslabel',
        filtrable: 'modstatus',
        filterComponent: (
          <ParListSelect parcode="MODSTAT" style={{ width: '100%' }} />
        ),
        width: 150,
      },
      {
        dataIndex: 'creationdate',
        filtrable: false,
        width: 150,
        render: (date) =>
          moment(date).isValid() && moment(date).format('YYYY-MM-DD HH:mm:ss'),
      },
      { dataIndex: 'creationusername', filtrable: false, width: 200 },
      {
        dataIndex: 'reviewdate',
        filtrable: false,
        width: 150,
        render: (date) =>
          moment(date).isValid() && moment(date).format('YYYY-MM-DD HH:mm:ss'),
      },
      { dataIndex: 'reviewusername', filtrable: false, width: 180 },
      {
        dataIndex: 'modexeclog',
        filtrable: false,
        width: 200,
        ellipsis: {
          showTitle: false,
        },
        render: (modexeclog) => (
          <Popover
            placement="bottomRight"
            style={{ width: 200 }}
            content={<div style={{ width: 300 }}>{modexeclog}</div>}
          >
            <a>{modexeclog && String(modexeclog)?.substring(0, 30) + ' ...'}</a>
          </Popover>
        ),
      },

      {
        key: 'action',

        render: (_, record) => {
          return record.modtype === 'EVENT' && record.modstatus === 'VALID' ? (
            <TabFinModificationRestoreModal
              objectid={record.ctrid}
              modId={record.modid}
              modevent={record.modevent}
              modstatus={record.modstatus}
              modctreference={record.ctreference}
              onSelect={handleCtridSelect}
              isActive={isActive}
            />
          ) : // <RestoreValidateButtons
          //   modid={record.modid}
          //   ctrid={record.ctrid}
          //   modevent={record.modevent}
          //   status={record.modstatus}
          //   refetch={refetch}
          // />
          null;
        },
        width: isTab ? 'hide' : 120,
        fixed: 'right',
      },
    ],
    [refetch]
  );

  return (
    <DF.Provider pagcode="finmodification">
      <Form form={form}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const finmodifications: FinModificationInterface[] =
              getFieldValue(fieldName);
            return (
              <DataGrid
                rowKey="id"
                isLoading={isFetching}
                titleComcode="jj"
                dataSource={finmodifications}
                columns={columns}
                onSearch={(values) => {
                  setSearchParams(values);
                  setsearchs(true);
                }}
                hideFilters={hideFilters}
                initialSearchValues={initialSearchValues}
                pagination={{ pageSize: 200 }}
                scroll={{ x: 1800, y: 400 }}
                extraActions={
                  isTab && (
                    <TabFinModificationRestoreModal isActive={isActive} objectid={ctrid} isTab />
                  )
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default FinModification;
